import { JwtHelperService } from '@auth0/angular-jwt';
import { currentAppToken } from './app-token';

/** `typ` claim the IAM puts on the app token it issues for a share-link item (`TokenType.GUEST_ACCESS`). */
const GUEST_ACCESS_TOKEN_TYPE: string = 'GUEST_ACCESS';

const jwtHelper: JwtHelperService = new JwtHelperService();

/**
 * Whether the current session is a share-link guest's: an account let in for one shared item
 * (`/s/:id`) rather than for a tenant, so it has no business in the rest of the app.
 *
 * Read off the app token's `typ` claim, which the IAM sets to `GUEST_ACCESS` when it exchanges a
 * share-link auth code — not off the missing tenant, which a member also has mid tenant selection.
 * An absent, unreadable or expired token is not a guest session: it is no session at all, and the
 * regular sign-in / clean-up paths own it.
 */
export function isGuestSession(): boolean {
  return guestSessionEmail() !== null;
}

/**
 * The guest's email (the token's `sub`) when {@link isGuestSession} holds, else `null` — read off
 * the token so callers outside the auth SDK's reach (and its tests) need no bridge.
 */
export function guestSessionEmail(): string | null {
  const token: string | null = currentAppToken();
  if (!token) {
    return null;
  }
  try {
    if (jwtHelper.isTokenExpired(token)) {
      return null;
    }
    const claims: { typ?: unknown; sub?: unknown } | null = jwtHelper.decodeToken(token);
    return claims?.typ === GUEST_ACCESS_TOKEN_TYPE && typeof claims.sub === 'string' ? claims.sub : null;
  } catch {
    return null;
  }
}
